import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  Container,
  Group,
  List,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { PublicShell } from '../components/PublicShell';
import { trpc } from '../lib/trpc';

const formatMinor = (minor: number, currency: string) => {
  if (minor === 0) return 'Free';
  return `${currency} ${(minor / 100).toLocaleString()}`;
};

// Explicit row shape — fully expanded type breaks the deep generic
// recursion that tRPC + Mantine SimpleGrid otherwise produce here.
interface PlanRow {
  id: number;
  name: string;
  trialDays: number;
  priceMinor: number;
  currency: string;
  interval: string;
  features: unknown;
}

function PricingPage() {
  const plans = trpc.billing.plans.list.useQuery({ activeOnly: true });
  const planRows = (plans.data ?? []) as readonly PlanRow[];

  return (
    <PublicShell>
      <Container size="lg" py="xl">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Stack gap="md" align="center">
            <Title order={2}>Pricing</Title>
            <Text c="dimmed" ta="center" maw={620}>
              Pick a plan, scale as you grow. Annual billing and bank-transfer pricing available on
              request — see our <Link to="/contact">contact page</Link>.
            </Text>

            {plans.isPending ? (
              <Skeleton height={300} w="100%" />
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md" w="100%">
                {planRows.map((p) => (
                  <Card key={p.id} withBorder style={{ display: 'flex', flexDirection: 'column' }}>
                    <Group justify="space-between">
                      <Title order={4}>{p.name}</Title>
                      {p.trialDays > 0 && (
                        <Badge color="green" variant="light" size="xs">
                          {p.trialDays}d trial
                        </Badge>
                      )}
                    </Group>
                    <Title order={2} mt={4}>
                      {formatMinor(p.priceMinor, p.currency)}
                    </Title>
                    <Text size="xs" c="dimmed">
                      per {p.interval}
                    </Text>
                    <List
                      spacing="xs"
                      size="sm"
                      mt="sm"
                      icon={<IconCheck size={14} color="var(--mantine-color-green-6)" />}
                      style={{ flex: 1 }}
                    >
                      {(p.features as string[]).map((f) => (
                        <List.Item key={f}>{f}</List.Item>
                      ))}
                    </List>
                    <Button mt="md" renderRoot={(props) => <Link to="/auth/register" {...props} />}>
                      Start with {p.name}
                    </Button>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Stack>
        </motion.div>
      </Container>
    </PublicShell>
  );
}

export const Route = createFileRoute('/pricing')({ component: PricingPage });
